import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { jobService } from "../services/database";

interface JobNotepadProps {
  jobId: string;
  initialContent?: string;
  organizationId: string;
}

export default function JobNotepad({
  jobId,
  initialContent = "",
  organizationId,
}: JobNotepadProps) {
  const [content, setContent] = useState(initialContent);
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  // Update content when initialContent changes
  useEffect(() => {
    setContent(initialContent || "");
  }, [initialContent]);

  // Mutation for updating job notes
  const updateNotesMutation = useMutation({
    mutationFn: async (notes: string) =>
      jobService.updateJob(jobId, { notes }, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job", jobId] });
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    updateNotesMutation.mutate(content);
  };

  const handleCancel = () => {
    setContent(initialContent || "");
    setIsEditing(false);
  };

  // Formatting functions
  const formatText = (format: string) => {
    const textarea = document.getElementById(
      "notepad-textarea"
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    let formattedText = "";
    switch (format) {
      case "bold":
        formattedText = `**${selectedText}**`;
        break;
      case "italic":
        formattedText = `*${selectedText}*`;
        break;
      case "underline":
        formattedText = `__${selectedText}__`;
        break;
      case "heading1":
        formattedText = `# ${selectedText}`;
        break;
      case "heading2":
        formattedText = `## ${selectedText}`;
        break;
      case "heading3":
        formattedText = `### ${selectedText}`;
        break;
      case "bullet":
        formattedText = `• ${selectedText}`;
        break;
      case "number":
        formattedText = `1. ${selectedText}`;
        break;
      case "code":
        formattedText = `\`${selectedText}\``;
        break;
      case "link":
        formattedText = `[${selectedText}](url)`;
        break;
      default:
        formattedText = selectedText;
    }

    const newContent =
      content.substring(0, start) + formattedText + content.substring(end);
    setContent(newContent);

    // Set cursor position after the formatted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + formattedText.length,
        start + formattedText.length
      );
    }, 0);
  };

  const insertText = (text: string) => {
    const textarea = document.getElementById(
      "notepad-textarea"
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const newContent =
      content.substring(0, start) + text + content.substring(start);
    setContent(newContent);

    // Set cursor position after inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  // Convert markdown to HTML for preview
  const renderMarkdown = (text: string) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/__(.*?)__/g, "<u>$1</u>")
      .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mb-2">$1</h1>')
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold mb-2">$1</h2>')
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold mb-2">$1</h3>')
      .replace(/^• (.*$)/gm, '<li class="ml-4">• $1</li>')
      .replace(/^\d+\. (.*$)/gm, '<li class="ml-4">$&</li>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
      .replace(
        /\[(.*?)\]\((.*?)\)/g,
        '<a href="$2" class="text-blue-600 hover:underline" target="_blank">$1</a>'
      )
      .replace(/\n/g, "<br>");
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border">
        <div className="flex gap-1">
          <button
            onClick={() => formatText("bold")}
            className="p-2 hover:bg-gray-200 rounded font-bold"
            title="Bold"
          >
            B
          </button>
          <button
            onClick={() => formatText("italic")}
            className="p-2 hover:bg-gray-200 rounded italic"
            title="Italic"
          >
            I
          </button>
          <button
            onClick={() => formatText("underline")}
            className="p-2 hover:bg-gray-200 rounded underline"
            title="Underline"
          >
            U
          </button>
        </div>

        <div className="w-px h-6 bg-gray-300"></div>

        <div className="flex gap-1">
          <button
            onClick={() => formatText("heading1")}
            className="p-2 hover:bg-gray-200 rounded text-sm font-bold"
            title="Heading 1"
          >
            H1
          </button>
          <button
            onClick={() => formatText("heading2")}
            className="p-2 hover:bg-gray-200 rounded text-sm font-bold"
            title="Heading 2"
          >
            H2
          </button>
          <button
            onClick={() => formatText("heading3")}
            className="p-2 hover:bg-gray-200 rounded text-sm font-bold"
            title="Heading 3"
          >
            H3
          </button>
        </div>

        <div className="w-px h-6 bg-gray-300"></div>

        <div className="flex gap-1">
          <button
            onClick={() => formatText("bullet")}
            className="p-2 hover:bg-gray-200 rounded"
            title="Bullet List"
          >
            •
          </button>
          <button
            onClick={() => formatText("number")}
            className="p-2 hover:bg-gray-200 rounded"
            title="Numbered List"
          >
            1.
          </button>
        </div>

        <div className="w-px h-6 bg-gray-300"></div>

        <div className="flex gap-1">
          <button
            onClick={() => formatText("code")}
            className="p-2 hover:bg-gray-200 rounded font-mono text-sm"
            title="Code"
          >
            &lt;/&gt;
          </button>
          <button
            onClick={() => formatText("link")}
            className="p-2 hover:bg-gray-200 rounded"
            title="Link"
          >
            🔗
          </button>
        </div>

        <div className="w-px h-6 bg-gray-300"></div>

        <div className="flex gap-1">
          <button
            onClick={() => insertText("\n---\n")}
            className="p-2 hover:bg-gray-200 rounded text-sm"
            title="Horizontal Rule"
          >
            HR
          </button>
          <button
            onClick={() => insertText("\n> ")}
            className="p-2 hover:bg-gray-200 rounded text-sm"
            title="Quote"
          >
            Quote
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="border rounded-lg overflow-hidden">
        {isEditing ? (
          <div>
            <textarea
              id="notepad-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-96 p-4 border-0 resize-none focus:outline-none focus:ring-0"
              placeholder="Start typing your notes here..."
            />
            <div className="flex justify-end gap-2 p-3 bg-gray-50 border-t">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateNotesMutation.isPending}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {updateNotesMutation.isPending ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Notes</h3>
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-1 bg-primary text-white rounded-md hover:bg-primary/90 text-sm"
              >
                Edit
              </button>
            </div>
            <div className="prose max-w-none">
              {content ? (
                <div
                  className="text-gray-700"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
                />
              ) : (
                <p className="text-gray-500 italic">
                  No notes yet. Click "Edit" to add some notes.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
